import React, { useEffect, useRef, useState } from 'react';
import { Animated, Pressable, StyleSheet, Text, View } from 'react-native';
import { Path, Svg } from 'react-native-svg';
import { fontFamilies } from '../../tokens';
import { TypeIcon } from './TypeIcon';
import { typeColors, typeLabels, TypeVariant } from './types';

export interface TypeDropdownProps {
  /** Currently selected type */
  value: TypeVariant;
  /** Callback when type is changed */
  onChange?: (type: TypeVariant) => void;
  /** Whether to show label (true) or just icon (false) */
  showLabel?: boolean;
  /** Whether the dropdown is disabled */
  disabled?: boolean;
  /** Test ID for testing */
  testID?: string;
  /** Available types to show in dropdown (defaults to all BASE_TYPES) */
  availableTypes?: TypeVariant[];
}

// Chevron Down icon (8px version for dropdown)
const ChevronDownIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 8,
  color = '#FFFFFF',
}) => (
  <Svg width={size} height={size} viewBox="0 0 8 8" fill="none">
    <Path
      d="M1 2.5L4 5.5L7 2.5"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

// Base types for the dropdown (excluding aliases)
const BASE_TYPES: TypeVariant[] = ['attention', 'blocker', 'done', 'routines', 'note', 'campaign'];

/**
 * TypeDropdown - Dropdown for selecting type status
 *
 * From Figma Crop Circle System
 * - Types Dropdown (icon only): Node 583-1627
 * - Type Dropdown Options (with label): Node 1312-1014
 *
 * Specs from Figma:
 * - Background: Type color
 * - Corner radius: 4px
 * - Icon-only: Padding 12px horizontal, 4px vertical, gap 6px
 * - With label: Padding 8px horizontal, 4px vertical, gap 4px
 * - Font: Inter 400 12px, white
 */
export const TypeDropdown: React.FC<TypeDropdownProps> = ({
  value,
  onChange,
  showLabel = false,
  disabled = false,
  testID,
  availableTypes = BASE_TYPES,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownAnim = useRef(new Animated.Value(1)).current;
  const chevronAnim = useRef(new Animated.Value(1)).current;

  const backgroundColor = typeColors[value];
  const label = typeLabels[value];

  // Filter out the currently selected type from dropdown options
  const dropdownTypes = availableTypes.filter((type) => type !== value);

  useEffect(() => {
    Animated.parallel([
      Animated.timing(dropdownAnim, {
        toValue: isOpen ? 1 : 0,
        duration: 200,
        useNativeDriver: true,
      }),
      Animated.timing(chevronAnim, {
        toValue: isOpen ? 1 : 0,
        duration: 200,
        useNativeDriver: true,
      }),
    ]).start();
  }, [isOpen, dropdownAnim, chevronAnim]);

  const handleSelect = (type: TypeVariant) => {
    onChange?.(type);
    setIsOpen(false);
  };

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  // When open, always show label; when closed, respect showLabel prop
  const shouldShowLabel = isOpen && showLabel;

  const chevronRotation = chevronAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '180deg'],
  });

  const dropdownOpacity = dropdownAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 1],
  });

  const dropdownTranslateY = dropdownAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [-8, 0],
  });

  // Determine trigger style based on showLabel prop (not isOpen state)
  // This keeps height consistent between open/closed states
  const triggerStyle = showLabel ? styles.triggerWithLabel : styles.triggerIconOnly;

  return (
    <View style={[styles.container, { position: 'relative' }]}>
      <Pressable
        style={[
          styles.trigger,
          triggerStyle,
          { backgroundColor },
          disabled && styles.disabled,
        ]}
        onPress={handleToggle}
        disabled={disabled}
        testID={testID}
      >
        <TypeIcon variant={value} size={16} color="#FFFFFF" />
        {shouldShowLabel && <Text style={styles.label}>{label}</Text>}
        <Animated.View style={{ transform: [{ rotate: chevronRotation }] }}>
          <ChevronDownIcon size={8} color="#FFFFFF" />
        </Animated.View>
      </Pressable>

      {isOpen && (
        <Animated.View
          style={[
            styles.dropdownMenu,
            {
              opacity: dropdownOpacity,
              transform: [{ translateY: dropdownTranslateY }],
            },
          ]}
        >
          {dropdownTypes.map((type) => (
            <Pressable
              key={type}
              style={[styles.menuItem, { backgroundColor: typeColors[type] }]}
              onPress={() => handleSelect(type)}
            >
              <TypeIcon variant={type} size={16} color="#FFFFFF" />
              <Text style={styles.menuItemLabel}>{typeLabels[type]}</Text>
            </Pressable>
          ))}
        </Animated.View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignSelf: 'flex-start',
  },
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 4,
  },
  triggerIconOnly: {
    paddingHorizontal: 12,
    paddingVertical: 12,
    gap: 6,
  },
  triggerWithLabel: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    gap: 4,
  },
  label: {
    fontSize: 12,
    fontFamily: fontFamilies.primary,
    fontWeight: '400',
    color: '#FFFFFF',
  },
  disabled: {
    opacity: 0.5,
  },
  dropdownMenu: {
    marginTop: 45,
    backgroundColor: '#FFFFFF',
    borderRadius: 4,
    padding: 4,
    gap: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
    position: 'absolute',
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 4,
    gap: 8,
    minWidth: 110,
  },
  menuItemLabel: {
    flex: 1,
    fontSize: 12,
    fontFamily: fontFamilies.primary,
    fontWeight: '400',
    color: '#FFFFFF',
    textAlignVertical: 'center',
  },
});

TypeDropdown.displayName = 'TypeDropdown';
